/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useContext, useEffect, useState } from "react";
import { ISchemaConstStringModel, TGenericModelNode } from "@adapptio/lib-blueprint";
import { FieldCode } from "@adapptio/lib-editor-ui";
import { IInspectorControls, TInspectorControlComponent } from "../InspectorTypes";
import { useBlueprintModel } from "../../../hooks/useBlueprintModel";
import { InvalidModelNode } from "../InvalidModelNode";
import { FieldResource } from "../../Fields/FieldResource";
import { RESOURCE_TYPES } from "@adapptio/lib-core";
import { FIELD_BUTTON_STYLE, FieldButton } from "../../Fields/FieldButton";
import { EditorMainContext } from "../../../contexts/EditorMainContext";
import { FieldRouteSelector } from "../../Fields/FieldRouteSelector";
import { useSimpleEvent } from "../../../hooks/useSimpleEvent";
import { FieldIconSelector } from "../../Fields/FieldIconSelector";
import { offEvent, onEvent } from "@adapptio/lib-shared";
import { useEditorInspectorContext } from "../../../contexts/EditorInspectorContext";
import { FloatingPanelExpressionEditorModel } from "../../FloatingPanelExpressionEditor/FloatingPanelExpressionEditorModel";
import { useFloatingPanel } from "../../../hooks/useFloatingPanel";
import { ICodeEditorProps } from "@adapptio/lib-editor-ui";

const AllowedResourceTypes_Action = [ RESOURCE_TYPES.ACTION ];
const AllowedResourceTypes_View = [ RESOURCE_TYPES.VIEW ];

const codeTypeLanguage: Record<string, ICodeEditorProps["language"]> = {
	codeJavaScript: "javascript",
	codeJson: "json",
	codeYaml: "yaml",
	codeXML: "xml",
	codeHTML: "html",
	codeCSS: "css"
};

export const ControlConstString = (_model: TGenericModelNode): IInspectorControls => {
	const model = _model as ISchemaConstStringModel;

	if (model.schema.opts.editorOptions?.controlType === "actionSelector") {
		return {
			primary: {
				component: ControlConstString_PrimaryActionSelector,
				model: model
			}
		};
	} else if (model.schema.opts.editorOptions?.controlType === "viewSelector") {
		return {
			primary: {
				component: ControlConstString_PrimaryViewSelector,
				model: model
			}
		};
	} else if (model.schema.opts.editorOptions?.controlType === "routeSelector") {
		return {
			primary: {
				component: ControlConstString_PrimaryRouteSelector,
				model: model
			}
		};
	} else if (model.schema.opts.editorOptions?.controlType === "iconSelector") {
		return {
			primary: {
				component: ControlConstString_PrimaryIconSelector,
				model: model
			}
		};
	} else if (codeTypeLanguage[model.schema.opts.editorOptions?.controlType]) {
		return {
			primary: {
				component: ControlConstString_PrimaryCode,
				model: model
			}
		};
	} else {
		return {
			primary: {
				component: ControlConstString_Primary,
				model: model
			}
		};
	}
};

export const ControlConstString_Primary: TInspectorControlComponent<ISchemaConstStringModel> = ({
	model: _model,
	additionalValidationErrors,
	id,
	label,
	path
}) => {
	const { model, validationErrors } = useBlueprintModel(_model, "constString");
	const [ expLastValue, setExpLastValue ] = useState<{ result: unknown }>(
		model?.expLastValue as { result: unknown }
	);
	const inspectorContext = useEditorInspectorContext();

	const { togglePanel, opened } = useFloatingPanel(
		FloatingPanelExpressionEditorModel,
		`panel-${model?.nodeId}`,
		{
			model: _model,
			path,
			readOnly: inspectorContext?.readOnly,
			debounceTimeout: inspectorContext?.inputDebounceTimeout,
			title: label,
			language: "adapptioExpressionInterpolation",
			defaultRect: { width: 500, height: 400 },
			showStringValue: !model.schema.opts.allowInterpolation || !model.isExpression
		}
	);

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	useEffect(() => {
		if (model && model.schema.opts.allowInterpolation) {
			const handleLastValueChange = () => setExpLastValue(model.expLastValue as { result: unknown });

			onEvent(model.expLastValueChangeEvent, handleLastValueChange);

			return () => {
				offEvent(model.expLastValueChangeEvent, handleLastValueChange);
			};
		}
	}, [ model ]);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	if (model.schema.opts.allowInterpolation) {
		return (
			<FieldCode
				key={model.nodeId}
				id={id}
				label={label}
				value={model.value}
				onChange={handleChange}
				validationErrors={validationErrors.concat(additionalValidationErrors || [])}
				parseErrors={model.expParseErrors}
				resultData={model.isExpression ? expLastValue?.result : model.value}
				grow={true}
				language="adapptioExpressionInterpolation"
				readOnly={inspectorContext?.readOnly}
				debounceTimeout={inspectorContext?.inputDebounceTimeout}
				canExpand
				onExpand={togglePanel}
				isExpanded={opened}
			/>
		);
	} else {
		return (
			<FieldCode
				key={model.nodeId}
				id={id}
				label={label}
				value={model.value}
				onChange={handleChange}
				validationErrors={validationErrors.concat(additionalValidationErrors || [])}
				grow={true}
				readOnly={inspectorContext?.readOnly}
				debounceTimeout={inspectorContext?.inputDebounceTimeout}
				canExpand
				onExpand={togglePanel}
				isExpanded={opened}
			/>
		);
		// return <FieldText
		// 	value={model.value}
		// 	onChange={handleChange}
		// 	validationErrors={validationErrors.concat(additionalValidationErrors || [])}
		// 	grow={true}
		// />;
	}
};

export const ControlConstString_PrimaryActionSelector: TInspectorControlComponent<ISchemaConstStringModel> =
	({ model: _model }) => {
		const editorContext = useContext(EditorMainContext);
		const inspectorContext = useEditorInspectorContext();

		const { model, validationErrors } = useBlueprintModel(_model, "constString");

		const createCommandId = editorContext.resourceService.getResourceCreateCommandId(
			RESOURCE_TYPES.ACTION
		);

		const handleChange = useCallback(
			(value: string) => {
				if (!model) {
					return;
				}

				model.schema.setValue(model, value, true);
			},
			[ model ]
		);

		const handleCreate = useCallback(() => {
			if (!createCommandId) {
				return;
			}

			editorContext.commandsService
				.execute(createCommandId)
				.then((resourceId: string) => {
					if (resourceId) {
						model.schema.setValue(model, resourceId, true);
					}
				})
				.catch((err) => {
					console.warn("Failed to create new action:", err);
				});
		}, [ editorContext, model ]);

		const handleOpen = useCallback(() => {
			editorContext.commandsService.execute("resource.open", model.value).catch((err) => {
				console.warn("Failed to open action resource:", err);
			});
		}, [ editorContext, model ]);

		const handleClear = useCallback(() => {
			model.schema.setValue(model, null, true);
		}, [ editorContext, model ]);

		if (!model) {
			return <InvalidModelNode model={_model} />;
		}

		return (
			<>
				<FieldResource
					value={model.value}
					onChange={handleChange}
					validationErrors={validationErrors}
					allowedResourceTypes={AllowedResourceTypes_Action}
					grow={true}
					readOnly={inspectorContext?.readOnly}
				/>
				{model.value && !model.schema.opts.constraints?.required ? (
					<>
						{/* @todo Translate */}
						<FieldButton
							icon="mdi/close"
							tooltip="Clear action"
							style={FIELD_BUTTON_STYLE.TRANSPARENT}
							onClick={handleClear}
						/>
					</>
				) : null}
				{model.value ? (
					<>
						{/* @todo Translate */}
						<FieldButton
							icon="mdi/open-in-new"
							tooltip="Open action"
							style={FIELD_BUTTON_STYLE.TRANSPARENT}
							onClick={handleOpen}
						/>
					</>
				) : null}
				{!model.value && createCommandId ? (
					<>
						{/* @todo Translate */}
						<FieldButton
							icon="mdi/plus"
							tooltip="Create new action"
							style={FIELD_BUTTON_STYLE.TRANSPARENT}
							onClick={handleCreate}
							disabled={inspectorContext?.readOnly}
						/>
					</>
				) : null}
			</>
		);
	};

export const ControlConstString_PrimaryViewSelector: TInspectorControlComponent<ISchemaConstStringModel> = ({
	model: _model
}) => {
	const editorContext = useContext(EditorMainContext);
	const inspectorContext = useEditorInspectorContext();

	const { model, validationErrors } = useBlueprintModel(_model, "constString");

	const createCommandId = editorContext.resourceService.getResourceCreateCommandId(RESOURCE_TYPES.VIEW);

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	const handleCreate = useCallback(() => {
		if (!createCommandId) {
			return;
		}

		editorContext.commandsService
			.execute(createCommandId)
			.then((resourceId: string) => {
				if (resourceId) {
					model.schema.setValue(model, resourceId, true);
				}
			})
			.catch((err) => {
				console.warn("Failed to create new view:", err);
			});
	}, [ editorContext, model ]);

	const handleOpen = useCallback(() => {
		editorContext.commandsService.execute("resource.open", model.value).catch((err) => {
			console.warn("Failed to open action resource:", err);
		});
	}, [ editorContext, model ]);

	const handleClear = useCallback(() => {
		model.schema.setValue(model, null, true);
	}, [ editorContext, model ]);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<>
			<FieldResource
				value={model.value}
				onChange={handleChange}
				validationErrors={validationErrors}
				allowedResourceTypes={AllowedResourceTypes_View}
				grow={true}
				readOnly={inspectorContext?.readOnly}
			/>
			{model.value && !model.schema.opts.constraints?.required ? (
				<>
					{/* @todo Translate */}
					<FieldButton
						icon="mdi/close"
						tooltip="Clear action"
						style={FIELD_BUTTON_STYLE.TRANSPARENT}
						onClick={handleClear}
					/>
				</>
			) : null}
			{model.value ? (
				<>
					{/* @todo Translate */}
					<FieldButton
						icon="mdi/open-in-new"
						tooltip="Open view"
						style={FIELD_BUTTON_STYLE.TRANSPARENT}
						onClick={handleOpen}
					/>
				</>
			) : null}
			{!model.value && createCommandId ? (
				// @todo Translate
				<FieldButton
					icon="mdi/plus"
					tooltip="Create new view"
					style={FIELD_BUTTON_STYLE.TRANSPARENT}
					onClick={handleCreate}
					disabled={inspectorContext?.readOnly}
				/>
			) : null}
		</>
	);
};

export const ControlConstString_PrimaryRouteSelector: TInspectorControlComponent<ISchemaConstStringModel> = ({
	model: _model
}) => {
	const { model, validationErrors } = useBlueprintModel(_model, "constString");
	const inspectorContext = useEditorInspectorContext();

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	const handleClear = useCallback(() => {
		model.schema.setValue(model, null, true);
	}, [ model ]);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<>
			<FieldRouteSelector
				value={model.value}
				onChange={handleChange}
				validationErrors={validationErrors}
				grow={true}
				readOnly={inspectorContext?.readOnly}
			/>
			{model.value && !model.schema.opts.constraints?.required ? (
				<>
					{/* @todo Translate */}
					<FieldButton
						icon="mdi/close"
						tooltip="Clear"
						style={FIELD_BUTTON_STYLE.TRANSPARENT}
						onClick={handleClear}
					/>
				</>
			) : null}
		</>
	);
};

export const ControlConstString_PrimaryIconSelector: TInspectorControlComponent<ISchemaConstStringModel> = ({
	model: _model
}) => {
	const editorContext = useContext(EditorMainContext);
	const inspectorContext = useEditorInspectorContext();

	const { model, validationErrors } = useBlueprintModel(_model, "constString");
	const [ iconPacks, setIconPacks ] = useState(editorContext.resourceService.getIconPacks());
	const [ urlMapping, setUrlMapping ] = useState(editorContext.resourceService.getIconPackUrlMapping());

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	const handleClear = useCallback(() => {
		model.schema.setValue(model, null, true);
	}, [ model ]);

	useSimpleEvent(
		editorContext.resourceService.onIconPacksChanged,
		() => {
			setIconPacks(editorContext.resourceService.getIconPacks());
			setUrlMapping(editorContext.resourceService.getIconPackUrlMapping());
		},
		[ editorContext.resourceService ]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<>
			<FieldIconSelector
				value={model.value}
				iconPacks={iconPacks}
				urlMapping={urlMapping}
				onChange={handleChange}
				validationErrors={validationErrors}
				grow={true}
				readOnly={inspectorContext?.readOnly}
				// @todo Translate
				placeholder="Select icon"
			/>
			{model.value && !model.schema.opts.constraints?.required ? (
				<>
					{/* @todo Translate */}
					<FieldButton
						icon="mdi/close"
						tooltip="Clear"
						style={FIELD_BUTTON_STYLE.TRANSPARENT}
						onClick={handleClear}
					/>
				</>
			) : null}
		</>
	);
};

export const ControlConstString_PrimaryCode: TInspectorControlComponent<ISchemaConstStringModel> = ({
	model: _model,
	additionalValidationErrors,
	id,
	label,
	path
}) => {
	const { model, validationErrors } = useBlueprintModel(_model, "constString");
	const inspectorContext = useEditorInspectorContext();

	const language = codeTypeLanguage[model.schema.opts.editorOptions?.controlType];

	const { togglePanel, opened } = useFloatingPanel(
		FloatingPanelExpressionEditorModel,
		`panel-${model?.nodeId}`,
		{
			model: _model,
			path,
			readOnly: inspectorContext?.readOnly,
			debounceTimeout: inspectorContext?.inputDebounceTimeout,
			title: label,
			language,
			defaultRect: { width: 500, height: 400 }
		}
	);

	const handleChange = useCallback(
		(value: string) => {
			if (!model) {
				return;
			}

			model.schema.setValue(model, value, true);
		},
		[ model ]
	);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	return (
		<FieldCode
			id={id}
			label={label}
			value={model.value}
			onChange={handleChange}
			validationErrors={validationErrors.concat(additionalValidationErrors || [])}
			grow={true}
			language={language}
			readOnly={inspectorContext?.readOnly}
			debounceTimeout={inspectorContext?.inputDebounceTimeout}
			canExpand
			onExpand={togglePanel}
			isExpanded={opened}
		/>
	);
};
